import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';

/**
 * api/client.ts
 * =============
 *
 * Single axios instance for the medical frontend.
 *
 * Auth strategy: Django session cookies. Same `sessionid` + `csrftoken` as
 * the rest of MyBooking. The server handles login flows (existing
 * mybooking_ai login pages); we just ride along.
 *
 * What this module enforces:
 *   1. Every mutating request gets `X-CSRFToken` from the `csrftoken` cookie.
 *   2. Every response is mapped to a friendly `ApiError` so callers don't
 *      have to deal with axios specifics.
 *   3. 401 responses bubble up an `auth-required` event so the auth provider
 *      can redirect to login.
 *   4. 403 responses surface the DRF `detail` if present.
 *   5. Network failures and 5xx are normalized.
 *
 * The base URL is empty in dev (Vite proxies `/api` to Django) and empty in
 * production (we ship the SPA from the same Django origin).
 */

// ─────────────────────────────────────────────────────────────────────────────
// Errors
// ─────────────────────────────────────────────────────────────────────────────

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const AUTH_REQUIRED_EVENT = 'mybooking-medical:auth-required';

// ─────────────────────────────────────────────────────────────────────────────
// CSRF cookie reader
// ─────────────────────────────────────────────────────────────────────────────

const getCookie = (name: string): string | null => {
  const prefix = `${name}=`;
  for (const part of document.cookie.split('; ')) {
    if (part.startsWith(prefix)) {
      return decodeURIComponent(part.slice(prefix.length));
    }
  }
  return null;
};

const isCsrfSafe = (method?: string): boolean => {
  // Per RFC, GET/HEAD/OPTIONS/TRACE shouldn't change state and skip CSRF
  return !method || ['GET', 'HEAD', 'OPTIONS', 'TRACE'].includes(method.toUpperCase());
};

// ─────────────────────────────────────────────────────────────────────────────
// Instance
// ─────────────────────────────────────────────────────────────────────────────

const instance: AxiosInstance = axios.create({
  baseURL: '',
  withCredentials: true,
  headers: { 'X-Requested-With': 'XMLHttpRequest' },
  timeout: 30_000,
});

instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (!isCsrfSafe(config.method)) {
    const token = getCookie('csrftoken');
    if (token) {
      config.headers.set('X-CSRFToken', token);
    }
  }
  return config;
});

instance.interceptors.response.use(
  (response) => response,
  (error: AxiosError<{ detail?: string; [k: string]: unknown }>) => {
    // Network or CORS — no response object
    if (!error.response) {
      throw new ApiError(
        'Network error. Please check your connection and try again.',
        0,
        'NETWORK',
      );
    }

    const status = error.response.status;
    const data = error.response.data ?? {};
    const detail = (typeof data === 'object' && data && 'detail' in data
      ? String(data.detail)
      : undefined) ?? error.message ?? 'Request failed';

    if (status === 401) {
      window.dispatchEvent(new CustomEvent(AUTH_REQUIRED_EVENT));
      throw new ApiError('Sign-in required.', 401, 'UNAUTHORIZED', data);
    }
    if (status === 403) {
      throw new ApiError(detail, 403, 'FORBIDDEN', data);
    }
    if (status === 404) {
      throw new ApiError(detail || 'Not found.', 404, 'NOT_FOUND', data);
    }
    if (status === 409) {
      throw new ApiError(detail || 'Conflict.', 409, 'CONFLICT', data);
    }
    if (status === 410) {
      throw new ApiError(detail || 'Resource is gone.', 410, 'GONE', data);
    }
    if (status === 422 || status === 400) {
      throw new ApiError(detail, status, 'VALIDATION', data);
    }
    if (status === 429) {
      throw new ApiError('Too many requests. Please try again shortly.', 429, 'RATE_LIMITED', data);
    }
    if (status >= 500) {
      throw new ApiError('Server error. Our team has been notified.', status, 'SERVER', data);
    }
    throw new ApiError(detail, status, 'OTHER', data);
  },
);

// ─────────────────────────────────────────────────────────────────────────────
// Public surface
// ─────────────────────────────────────────────────────────────────────────────

export const api = {
  get<T>(url: string, config?: AxiosRequestConfig) {
    return instance.get<T>(url, config).then((r) => r.data);
  },
  post<T>(url: string, body?: unknown, config?: AxiosRequestConfig) {
    return instance.post<T>(url, body, config).then((r) => r.data);
  },
  patch<T>(url: string, body?: unknown, config?: AxiosRequestConfig) {
    return instance.patch<T>(url, body, config).then((r) => r.data);
  },
  put<T>(url: string, body?: unknown, config?: AxiosRequestConfig) {
    return instance.put<T>(url, body, config).then((r) => r.data);
  },
  delete<T>(url: string, config?: AxiosRequestConfig) {
    return instance.delete<T>(url, config).then((r) => r.data);
  },
};

export type DRFPaginated<T> = {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
};
